import { useSelector } from 'react-redux'
import { getUseNormalTaxRounding } from '../../selectors/menus'
import { getTaxClasses, getTaxRules } from '../Selectors'
import { useMemo } from 'react'
import { getOrderInProgress } from '../../selectors/order'
import { getRemoteOrderTotal } from '../../selectors/orderTotalRemote'
import { calculateServiceCharge, calculateServiceFeeTaxRules, calculateTaxClasses } from '../../utils/totalUtils'
import { getMenuServiceCharge } from '../../selectors/items'
import { getCartItems, getSubtotal } from '../../selectors/cart'

const useTaxResults = () => {
  const orderInProgress = useSelector(getOrderInProgress)
  const remoteOrderTotal = useSelector(getRemoteOrderTotal)
  const lineItems = useSelector(getCartItems)
  const subtotal = useSelector(getSubtotal)
  const useNormalTaxRounding = useSelector(getUseNormalTaxRounding)
  const taxClasses = useSelector(getTaxClasses)
  const taxRules = useSelector(getTaxRules)
  const serviceCharge = useSelector(getMenuServiceCharge)

  const getTaxTotalFromBreakdown = (taxRulesBreakdown) => {
    //fist filter out inclusive tax rules
    const exclusiveTaxRules = Object.values(taxRulesBreakdown).filter((rule) => !rule.inclusive)

    //then sum the tax amounts of the remaining rules
    return (
      exclusiveTaxRules.reduce((acc, curr) => {
        return acc + (curr.taxAmountInCents ?? curr.taxRuleAmountInCents ?? 0)
      }, 0) || 0
    )
  }

  const taxResults = useMemo(() => {
    if (orderInProgress?.uuid) {
      return {
        taxAmountInCents: orderInProgress?.taxAmountInCents || 0,
        taxRulesBreakdown: orderInProgress?.taxRulesBreakdown || {},
        exemptedTaxAmountInCents: getTaxTotalFromBreakdown(orderInProgress?.exemptableTaxRules || {}),
      }
    }

    if (remoteOrderTotal?.isAvailable) {
      return {
        taxAmountInCents: remoteOrderTotal?.taxAmountInCents || 0,
        taxRulesBreakdown: remoteOrderTotal?.taxRulesBreakdown || {},
        exemptedTaxAmountInCents: getTaxTotalFromBreakdown(remoteOrderTotal?.exemptableTaxRules || {}),
      }
    }

    const serviceFee = {
      name: serviceCharge?.name,
      total: calculateServiceCharge(serviceCharge, subtotal) ?? 0,
      legal: serviceCharge?.legal,
      taxClassUuid: serviceCharge?.taxClassUuid,
    }

    const calculateTaxResult = calculateTaxClasses(lineItems, useNormalTaxRounding, taxClasses, taxRules)

    const calculateServiceFeeTaxRulesResult = calculateServiceFeeTaxRules(
      calculateTaxResult?.taxRulesBreakdown || {},
      serviceFee,
      useNormalTaxRounding,
      taxClasses,
      taxRules,
      false, // tax exempt will always be false when calculating tax in this hook
    )
    const serviceFeeTaxAmountInCents = calculateServiceFeeTaxRulesResult?.serviceFeeTaxAmountInCents || 0
    const taxRulesBreakdown = calculateServiceFeeTaxRulesResult?.taxRulesBreakdown || {}

    const taxAmountInCents = calculateTaxResult.taxAmountInCents + serviceFeeTaxAmountInCents

    return {
      taxAmountInCents,
      taxRulesBreakdown,
      exemptedTaxAmountInCents: getTaxTotalFromBreakdown(orderInProgress?.exemptableTaxRules || {}),
    }
  }, [
    lineItems,
    orderInProgress,
    remoteOrderTotal?.isAvailable,
    remoteOrderTotal?.taxAmountInCents,
    remoteOrderTotal?.taxRulesBreakdown,
    remoteOrderTotal?.exemptableTaxRules,
    serviceCharge,
    subtotal,
    taxClasses,
    taxRules,
    useNormalTaxRounding,
  ])

  return {
    taxAmountInCents: taxResults.taxAmountInCents,
    taxRulesBreakdown: taxResults.taxRulesBreakdown,
    exemptedTaxAmountInCents: taxResults.exemptedTaxAmountInCents,
  }
}

export default useTaxResults
